import config from '@root/src/config';
import { isArrayOfObjects, isEmpty, isObject } from '@root/src/utils/commons/objects';
import FetchError from '@services/api/utils/FetchError';

type ApiErrorPayload = {
  code?: string;
  message?: string;
  status?: number;
  details?: unknown;
  errors?: Record<string, unknown>;
  error?: string;
};

const parseApiErrorPayload = (errorText: string): ApiErrorPayload | undefined => {
  if (!errorText) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(errorText) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }
    return parsed as ApiErrorPayload;
  } catch {
    return undefined;
  }
};

const normalizeApiErrorPayload = (
  response: Response,
  payload?: ApiErrorPayload,
  fallbackMessage?: string,
): { message: string; code?: string; errorData: ApiErrorPayload } => {
  const messageFromPayload = typeof payload?.message === 'string' ? payload.message : undefined;
  const messageFromLegacyField = typeof payload?.error === 'string' ? payload.error : undefined;
  const message =
    messageFromPayload ||
    messageFromLegacyField ||
    (fallbackMessage && fallbackMessage.trim() ? fallbackMessage : undefined) ||
    response.statusText ||
    'Something went wrong';
  const normalizedCode = typeof payload?.code === 'string' ? payload.code : undefined;

  return {
    message,
    code: normalizedCode,
    errorData: {
      ...payload,
      status: payload?.status ?? response.status,
      message,
      code: normalizedCode,
    },
  };
};

/**
 * Build and Fetch request
 * @param {string} endpoint - url
 * @param {RequestMethod} method - request method
 * @param {object} params - url entity id
 * @param {object} query - url params
 * @param {BackBody} body - request body
 * @param {AbortSignal} signal
 * @param {Function} responseFormatter - Function to format data from back to front
 */
export const fetchRequest = <BackEntity, FrontEntity, BackBody = never>({
  endpoint,
  method,
  responseFormatter,
  params,
  query,
  body,
  signal,
  urlApiNext = false,
}: {
  endpoint: string;
  method: RequestMethod;
  params?: object;
  query?: object;
  body?: BackBody;
  signal?: AbortSignal;
  urlApiNext?: boolean;
  responseFormatter?: (data: BackEntity) => FrontEntity;
}): Promise<FrontEntity> => {
  let url = endpoint;

  // Build params
  if (params && !isEmpty(params)) {
    url = Object.entries(params).reduce((s, [v, k]) => s.replace(new RegExp(`:${v}`, 'gi'), k), endpoint);
  }

  if (url[url.length - 1] !== '/') {
    url += '/';
  }

  // Build query
  if (query && !isEmpty(query)) {
    const res = Object.entries(query)
      .filter(([_, v]) => {
        if (typeof v === 'string') {
          return v.length >= 2;
        }
        if (Array.isArray(v)) {
          return v.length > 0;
        }
        return true;
      })
      .map(([k, v]) => {
        if (v == null) {
          return '';
        }
        if (Array.isArray(v)) {
          if (v.length === 0) {
            return `${k}=[]`;
          }
          if (isArrayOfObjects(v)) {
            return `${k}=${encodeURIComponent(JSON.stringify(v))}`;
          }
          return `${k}=${encodeURIComponent(v.join(','))}`;
        }
        if (isObject(v)) {
          return `${k}=${encodeURIComponent(JSON.stringify(v))}`;
        }
        return `${k}=${v as string}`;
      })
      .join('&');
    url += `?${res}`;
  }

  return callApi<FrontEntity, BackEntity, BackBody>({
    endpoint: url,
    method,
    body,
    signal,
    responseFormatter,
    urlApiNext,
  });
};

export enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

/**
 * Construct, send request and format result
 * @param {string} endpoint - url
 * @param {RequestMethod} method - request method
 * @param {string} token - token for secured url
 * @param {BackBody} body - request body
 * @param {AbortSignal} signal
 * @param {string} contentType - request contentType
 * @param {Function} responseFormatter - Function to format data from back to front
 * @param {boolean} urlApiNext - use urlApiNext instead of urlApi (used for next api routes)
 */
const callApi = async <FrontEntity, BackEntity, BackBody>({
  endpoint,
  method,
  body,
  signal,
  contentType = 'application/json',
  responseFormatter,
  urlApiNext,
}: {
  endpoint: string;
  method: RequestMethod;
  body?: BackBody;
  signal?: AbortSignal;
  contentType?: string;
  responseFormatter?: (data: BackEntity) => FrontEntity;
  urlApiNext?: boolean;
}): Promise<FrontEntity> => {
  if (!endpoint) {
    throw Error('endpoint is required');
  }
  if (!method) {
    throw Error('method is required');
  }

  const url = `${urlApiNext ? config.url.urlApiNext : config.url.urlApi}${endpoint}`;

  // Check if body is FormData - use instanceof and Object.prototype.toString for reliable detection
  const isFormData = body instanceof FormData || (body && Object.prototype.toString.call(body) === '[object FormData]');

  const headers = new Headers({
    Connection: 'keep-alive',
  });

  // Don't set Content-Type for FormData - let the browser set it with boundary
  if (!isFormData) {
    headers.set('Content-Type', contentType);
  }

  const options: Parameters<typeof fetch>[1] = {
    method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include', // Changed from 'same-origin' to 'include' for better privacy mode compatibility
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  };

  if (body) {
    // Don't stringify FormData - pass it directly
    options.body = isFormData ? (body as BodyInit) : (JSON.stringify(body) as BodyInit);
  }
  if (signal) {
    options.signal = signal;
  }

  if (config.devTools) {
    console.log('---------------- Fetch ------------------------------------------------------');
    console.log(url, options);
    console.log('-----------------------------------------------------------------------------');
  }

  // Check for privacy mode and log a warning
  const isPrivacyMode = (() => {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return false;
    } catch {
      return true;
    }
  })();

  if (isPrivacyMode) {
    console.warn('Privacy mode detected - localStorage unavailable, authentication may be affected');
  }

  const response = await fetch(url, options);
  if (!response.ok) {
    const errorText = await response.text();
    const payload = parseApiErrorPayload(errorText);
    const fallbackText = payload ? undefined : errorText;
    const { message, code, errorData } = normalizeApiErrorPayload(response, payload, fallbackText);
    throw new FetchError(response, message, code, errorData);
  }

  // Handle empty-body success responses gracefully.
  if (response.status === 204 || response.status === 205) {
    if (config.devTools) {
      console.debug('fetchRequest: successful void response', {
        endpoint,
        method,
        status: response.status,
      });
    }
    return responseFormatter ? responseFormatter(undefined as BackEntity) : (undefined as FrontEntity);
  }

  const responseContentType = response.headers.get('content-type') ?? '';
  if (!responseContentType.includes('application/json')) {
    if (config.devTools) {
      console.debug('fetchRequest: successful non-JSON response treated as void', {
        endpoint,
        method,
        status: response.status,
        contentType: responseContentType,
      });
    }
    return responseFormatter ? responseFormatter(undefined as BackEntity) : (undefined as FrontEntity);
  }

  // Some successful endpoints may still legitimately return an empty JSON body with 200.
  const text = await response.text();
  if (!text) {
    if (config.devTools) {
      console.debug('fetchRequest: successful empty JSON body treated as void', {
        endpoint,
        method,
        status: response.status,
      });
    }
    return responseFormatter ? responseFormatter(undefined as BackEntity) : (undefined as FrontEntity);
  }

  const data = JSON.parse(text) as BackEntity;
  return responseFormatter ? responseFormatter(data) : (data as unknown as FrontEntity);
};

/**
 * Fetch a blob response (for file downloads)
 * Uses the same authentication and error handling as callApi
 * @param endpoint - url endpoint
 * @param method - request method (default: GET)
 * @param urlApiNext - use urlApiNext instead of urlApi
 */
export const callApiBlob = async (
  endpoint: string,
  method: RequestMethod = RequestMethod.GET,
  urlApiNext = false,
): Promise<Blob> => {
  if (!endpoint) {
    throw Error('endpoint is required');
  }

  const url = `${urlApiNext ? config.url.urlApiNext : config.url.urlApi}${endpoint}`;

  const headers = new Headers({
    Connection: 'keep-alive',
  });

  const options: Parameters<typeof fetch>[1] = {
    method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  };

  if (config.devTools) {
    console.log('---------------- Fetch Blob ------------------------------------------------------');
    console.log(url, options);
    console.log('-----------------------------------------------------------------------------');
  }

  const response = await fetch(url, options);
  if (!response.ok) {
    const errorText = await response.text();
    const payload = parseApiErrorPayload(errorText);
    const fallbackText = payload ? undefined : errorText;
    const { message, code, errorData } = normalizeApiErrorPayload(response, payload, fallbackText);
    throw new FetchError(response, message, code, errorData);
  }

  return response.blob();
};
